/**
 * Páginas por ramo (`/para/[slug]`). Cada uma responde à busca de quem ainda
 * não conhece a Controlapag mas tem o problema: "agenda online para
 * barbearia", "como cobrar aluno de personal", "cliente que falta na
 * manicure". O texto precisa ser do ramo de verdade (dor, rotina, dúvidas e
 * os padrões que o assistente sugere) senão vira página-porta repetida.
 *
 * Preço, limite e taxa vêm de `content/facts.ts`. Os padrões do ramo vêm de
 * `content/generated/packs.json` (snapshot dos pacotes da API, gerado por
 * `scripts/build-packs-snapshot.mjs`). Os prazos citados no texto ficam em
 * `leads` e o teste `scripts/seo-segments.test.mjs` confere com o snapshot.
 * Nunca inventar estatística nem depoimento. "Sistema" só no title e no h1 (D5).
 */
import { FACTS_CHECKED_AT, FREE_PLAN, PROSE, REMINDERS, SOLO_PLANS, TEAM_PLAN, formatBRL } from '../facts';

export interface SegmentFaq {
  question: string;
  answer: string;
}

/** Qual print do app acompanha o passo do "como funciona". */
export type SegmentMockKind = 'booking' | 'chat' | 'charges';

export interface SegmentStep {
  title: string;
  sub: string;
  mock: SegmentMockKind;
}

export interface SegmentPage {
  slug: string;
  /** Nome curto do ramo, usado em links ("Barbearia"). */
  label: string;
  /** Nome do ramo no meio da frase ("barbearia", "salão de beleza"). */
  ramo: string;
  /** `<title>` base (até 60 caracteres); `pageTitle()` decide o sufixo. */
  title: string;
  /** 120 a 160 caracteres, dizendo o que é pago. */
  description: string;
  h1: string;
  /** Uma linha sob o H1. */
  sub: string;
  /** Bloco de resposta logo depois do H1: "X é/faz Y", com número, sem link. */
  answer: string;
  /** Como o ramo chama quem atende: "cliente", "aluno", "paciente". */
  clientWord: 'cliente' | 'aluno' | 'paciente';
  /** Pacote do assistente (`packs.json`) que alimenta "Como fica pronto". */
  packSlug: string;
  /** Prazos do pacote citados no texto (conferidos pelo teste). */
  leads: { cancelHours: number; reminderHours: number | null };
  /** Selos do hero, quando o padrão não serve ao ramo. */
  badges?: string[];
  /** Frase grande do meio da página (até ~6 palavras). */
  tagline: string;
  painsTitle: string;
  /** Primeira frase da seção: a resposta em até ~25 palavras. */
  painsIntro: string;
  /** O que dá errado hoje: rótulo curto e 1 frase de explicação. */
  pains: { icon: string; label: string; text: string }[];
  stepsTitle: string;
  /** Passos próprios do ramo; sem isso valem os 3 passos padrão. */
  steps?: SegmentStep[];
  featuresTitle: string;
  featuresIntro: string;
  /** Só recurso que existe. `paid` marca o que é dos planos pagos. */
  features: { icon: string; title: string; short: string; paid?: boolean }[];
  defaultsTitle: string;
  faqTitle: string;
  faq: SegmentFaq[];
  /** Dados de exemplo dos prints do app (página de agendamento, WhatsApp, cobrança). */
  mock: {
    business: string;
    place: string;
    icon: string;
    services: { name: string; price: string }[];
    /** Valor da cobrança no WhatsApp e no painel ("R$ 45,00"). Padrão: o 1º preço. */
    charge?: string;
    /** Hora em que o lembrete do horário sai (o horário do exemplo é 07/10 às 10:20). */
    reminderAt?: string;
    /** Linha extra do lembrete (o app põe o endereço ou o link da chamada). */
    reminderExtra?: string;
    /** `false` quando o ramo não tem lembrete de horário (transporte escolar). */
    appointmentReminder?: boolean;
    /** `monthly`: ramo sem agenda para o cliente marcar; o palco mostra a cobrança. */
    kind?: 'booking' | 'monthly';
  };
  /** Slugs de outros ramos para linkar no fim da página. */
  related: string[];
  /** Slugs de `/comparar` que respondem a próxima dúvida do ramo. */
  comparisons: string[];
}

// ------------------------------------------------------------ fatos usados

const FREE_LIMIT = FREE_PLAN.maxClientsPerMonth;
const FROM_PRICE = PROSE.fromPrice;
const PLAN_NAMES = `${SOLO_PLANS.slice(0, -1)
  .map((plan) => plan.name)
  .join(', ')} e ${SOLO_PLANS[SOLO_PLANS.length - 1].name}`;
const TEAM_FROM = formatBRL(TEAM_PLAN.fromPrice);
const TEAM_RANGE = `de ${TEAM_PLAN.minProfessionals} a ${TEAM_PLAN.maxProfessionals} profissionais`;
const NO_SHOW_DAYS = REMINDERS.noShowMarkWithinDays;
const CHARGE_DAYS = REMINDERS.chargeDaysBeforeOptions.join(' ou ');

/** "03/10/2026" a partir de "2026-10-03". */
export const formatDateBR = (iso: string): string => iso.split('-').reverse().join('/');

/** Data da conferência mostrada nas páginas por ramo. */
export const SEGMENTS_CHECKED_AT = formatDateBR(FACTS_CHECKED_AT);

const hours = (value: number) => `${value} ${value === 1 ? 'hora' : 'horas'}`;

const BELEZA = { cancelHours: 2, reminderHours: 24 };
const AULAS = { cancelHours: 12, reminderHours: 12 };
const SAUDE = { cancelHours: 24, reminderHours: 24 };

// --------------------------------------------- respostas com o mesmo fato

/** Quanto custa, com o recurso pago que mais pesa no ramo. */
const priceAnswer = (client: string, paidHighlight: string) =>
  `A conta grátis vale para até ${FREE_LIMIT} ${client} por mês, com agenda e cobrança avulsa. ${paidHighlight} estão nos planos ${PLAN_NAMES}, ${FROM_PRICE} no site.`;

const noDeposit = (client: string) =>
  `Não. A Controlapag não cobra sinal. A cobrança sai quando o horário é confirmado e vence no dia do atendimento; nos planos pagos ${client} recebe o link para pagar, com taxa por pagamento.`;

const noShow = (unit: string, extra = '') =>
  `Você marca a falta em até ${NO_SHOW_DAYS} dias depois do horário. ${unit[0].toUpperCase()}${unit.slice(1)} avulso fica com a cobrança em aberto; na mensalidade, a falta conta como sessão usada.${extra} A Controlapag não cria multa nem valor extra.`;

const payAnswer = (client: string) =>
  `Por padrão ${client} paga direto a você, no Pix, no dinheiro ou na maquininha, e você toca em Confirmar recebimento e confirma. Nos planos pagos, também dá para pagar pelo link, com taxa por pagamento: ${PROSE.pixFee} e ${PROSE.cardFee}.`;

const teamAnswer = (who: string, place: string) =>
  `Dá, no plano Equipe: cada ${who} tem a própria agenda e a página ${place} reúne todo mundo. Vale ${TEAM_RANGE}, sai ${TEAM_FROM} por mês com 2 e é contratado só pelo site.`;

export const SEGMENT_PAGES: SegmentPage[] = [
  {
    slug: 'barbearia',
    label: 'Barbearia',
    ramo: 'barbearia',
    title: 'Agenda online e cobrança para barbearia',
    description: `Link para o cliente marcar, lembrete no WhatsApp e assinatura de corte nos planos pagos. Grátis para até ${FREE_LIMIT} clientes com agenda e cobrança avulsa.`,
    h1: 'Agenda e cobrança para barbearia e barbeiro',
    sub: 'O cliente marca pelo link. O lembrete sai no WhatsApp.',
    answer: `A Controlapag é um app de agenda e cobrança para barbearia: o cliente marca pelo link, o lembrete sai no WhatsApp e a assinatura mensal de corte é cobrada sozinha. Grátis para até ${FREE_LIMIT} clientes por mês com cobrança avulsa; o resto nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'cliente',
    packSlug: 'barbearia',
    leads: BELEZA,
    tagline: 'A cadeira cheia e a conta em dia.',
    painsTitle: 'Por que a barbearia perde dinheiro com falta e fiado?',
    painsIntro:
      'Porque o horário vazio não volta e o fiado fica na memória: sem lembrete e sem registro, a cadeira para e a conta do mês não fecha.',
    pains: [
      { icon: 'person-remove-outline', label: 'Cliente some sem avisar', text: 'O horário fica vazio e ninguém mais consegue marcar em cima da hora.' },
      { icon: 'chatbubbles-outline', label: 'WhatsApp o dia todo', text: 'Você para o corte para responder "tem horário amanhã?" e perde o ritmo.' },
      { icon: 'book-outline', label: 'Fiado no caderno', text: 'O "pago semana que vem" fica anotado no papel e some no meio do mês.' },
      { icon: 'repeat-outline', label: 'Cliente fiel sem plano', text: 'Quem corta todo mês paga avulso, e o caixa depende de ele lembrar de voltar.' },
    ],
    stepsTitle: 'Como o cliente da barbearia marca e paga?',
    featuresTitle: 'O que a Controlapag faz pela barbearia?',
    featuresIntro:
      'Junta agenda, cobrança e lembrete num lugar só. A agenda e a cobrança avulsa são grátis; link, lembrete e assinatura estão nos planos pagos.',
    features: [
      { icon: 'calendar-outline', title: 'Agenda dos cortes', short: 'Horários de cada dia' },
      { icon: 'link-outline', title: 'Link de agendamento', short: 'Cliente marca sozinho', paid: true },
      { icon: 'repeat-outline', title: 'Assinatura mensal de corte', short: 'Cota de cortes por mês', paid: true },
      { icon: 'logo-whatsapp', title: 'Lembrete no WhatsApp', short: '1 lembrete por horário', paid: true },
      { icon: 'wallet-outline', title: 'Quem pagou e quem deve', short: 'Painel do mês' },
      { icon: 'people-outline', title: 'Vários barbeiros', short: 'Agenda por barbeiro', paid: true },
    ],
    defaultsTitle: 'Como a agenda da barbearia já começa pronta?',
    faqTitle: 'O que o barbeiro pergunta antes de começar?',
    faq: [
      {
        question: 'Como fazer plano mensal de corte na barbearia?',
        answer:
          'Crie uma assinatura com o valor e a quantidade de cortes por mês. A Controlapag gera a cobrança todo mês e conta quantos cortes o cliente já usou; o que sobra não passa para o mês seguinte. Assinatura é dos planos pagos.',
      },
      {
        question: 'Cliente faltou: posso cobrar?',
        answer: `Pode cobrar o que já estava combinado. ${noShow('o corte', ' Na assinatura, é um corte a menos no mês.')}`,
      },
      {
        question: 'Como diminuir falta de cliente na barbearia?',
        answer: `Com o lembrete no WhatsApp ${hours(BELEZA.reminderHours)} antes (planos pagos) e um prazo mínimo para desmarcar, que o assistente sugere em ${hours(BELEZA.cancelHours)} para barbearia. Depois desse prazo, o cliente não desmarca mais pelo link.`,
      },
      { question: 'Dá para cobrar sinal para garantir o horário?', answer: noDeposit('o cliente') },
      {
        question: 'Quanto custa um app de agenda para barbearia?',
        answer: priceAnswer('clientes', 'Link de agendamento, lembrete no WhatsApp e assinatura de corte'),
      },
      {
        question: 'O cliente precisa baixar app para marcar?',
        answer:
          'Não. Ele abre o link da sua página no navegador, escolhe o serviço, o barbeiro e um horário livre. A página com agenda está nos planos pagos, e o link vai na bio do Instagram.',
      },
      { question: 'Dá para ter mais de um barbeiro?', answer: teamAnswer('barbeiro', 'da barbearia') },
      {
        question: 'Barbeiro autônomo também pode usar?',
        answer: `Pode. Quem atende sozinho começa na conta grátis ou escolhe entre os planos ${PLAN_NAMES}. O Equipe é para quem tem 2 barbeiros ou mais.`,
      },
      { question: 'Como o cliente paga o corte?', answer: payAnswer('o cliente') },
    ],
    mock: {
      business: 'Barbearia Navalha',
      place: 'Vila Madalena',
      icon: 'cut',
      services: [
        { name: 'Corte', price: 'R$ 45' },
        { name: 'Corte e barba', price: 'R$ 75' },
      ],
    },
    related: ['salao-de-beleza', 'manicure', 'esteticista'],
    comparisons: ['agenda-no-whatsapp'],
  },
  {
    slug: 'salao-de-beleza',
    label: 'Salão de beleza',
    ramo: 'salão de beleza',
    title: 'Sistema de agendamento para salão de beleza',
    description: `Agenda por profissional e cobrança avulsa grátis para até ${FREE_LIMIT} clientes. Link para a cliente marcar e lembrete no WhatsApp nos planos pagos do salão.`,
    h1: 'Agendamento online para salão de beleza',
    sub: 'Cada profissional com sua agenda. A cliente marca pelo link.',
    answer: `A Controlapag é um app de agenda e cobrança para salão de beleza: cada profissional tem a própria agenda, a cliente marca pelo link e o lembrete sai no WhatsApp. Grátis para até ${FREE_LIMIT} clientes por mês; link e lembrete nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'cliente',
    packSlug: 'cabeleireiro',
    leads: BELEZA,
    tagline: 'Agenda certa, cadeira ocupada.',
    painsTitle: 'Por que o salão perde horário e dinheiro?',
    painsIntro:
      'Porque o encaixe é feito de cabeça e o horário longo que fica vazio custa a tarde: uma coloração que não acontece não se recupera.',
    pains: [
      { icon: 'chatbubbles-outline', label: 'Recepção presa no WhatsApp', text: 'Quem responde mensagem não atende a cliente que está na cadeira.' },
      { icon: 'git-merge-outline', label: 'Encaixe errado', text: 'Escova marcada em cima da coloração porque ninguém conferiu a duração.' },
      { icon: 'person-remove-outline', label: 'Falta em horário longo', text: 'Uma progressiva que não acontece deixa a tarde da cabeleireira parada.' },
      { icon: 'bar-chart-outline', label: 'Sem número por profissional', text: 'No fim do mês ninguém sabe quanto cada uma atendeu e recebeu.' },
    ],
    stepsTitle: 'Como a cliente do salão marca e paga?',
    featuresTitle: 'O que a Controlapag faz pelo salão de beleza?',
    featuresIntro:
      'Organiza a agenda pela duração de cada serviço e avisa a cliente no WhatsApp. A agenda e a cobrança avulsa são grátis.',
    features: [
      { icon: 'people-outline', title: 'Agenda por profissional', short: 'Uma agenda por pessoa', paid: true },
      { icon: 'globe-outline', title: 'Página do salão', short: 'Link único do salão', paid: true },
      { icon: 'logo-whatsapp', title: 'Aviso e lembrete no WhatsApp', short: 'Aprovou, a cliente é avisada', paid: true },
      { icon: 'time-outline', title: 'Duração de cada serviço', short: 'Sem encaixe por cima' },
      { icon: 'wallet-outline', title: 'Cobrança e caixa', short: 'Quem pagou, na hora' },
    ],
    defaultsTitle: 'Como a agenda do salão já começa pronta?',
    faqTitle: 'O que o salão pergunta antes de começar?',
    faq: [
      {
        question: 'Qual app de agendamento usar em salão de beleza pequeno?',
        answer: `Um que a cliente use sem baixar nada e que respeite a duração de cada serviço. Na Controlapag a cliente marca pelo link e o salão vê a agenda no celular ou no computador, ${PROSE.platforms}.`,
      },
      { question: 'Dá para cada profissional ter sua própria agenda?', answer: teamAnswer('profissional', 'do salão') },
      {
        question: 'Como colocar o agendamento no Instagram do salão?',
        answer:
          'Copie o link da página do salão e coloque na bio ou nos destaques. Quem clica vê os serviços, os preços e os horários livres. A página com agenda está nos planos pagos.',
      },
      {
        question: 'A cliente faltou na coloração: o que acontece?',
        answer: `${noShow('o serviço')} O prazo mínimo para desmarcar, que o assistente sugere em ${hours(BELEZA.cancelHours)}, barra a desistência de última hora pelo link.`,
      },
      { question: 'Dá para cobrar sinal da coloração ou da progressiva?', answer: noDeposit('a cliente') },
      {
        question: 'Dá para mostrar "a partir de" no preço?',
        answer:
          'Dá. Cada serviço mostra o valor exato, "a partir de" ou "Sob consulta" na página do salão. O assistente já sugere "a partir de" para coloração, luzes e progressiva.',
      },
      {
        question: 'Quanto custa a Controlapag para salão?',
        answer: `${priceAnswer('clientes', 'Link de agendamento e lembrete no WhatsApp')} Com 2 profissionais ou mais, o plano Equipe sai ${TEAM_FROM} por mês.`,
      },
      {
        question: 'A cliente precisa baixar app?',
        answer: 'Não. Ela marca pelo navegador do celular, no link do salão, e recebe o lembrete no WhatsApp (planos pagos).',
      },
    ],
    mock: {
      business: 'Studio Bela Rosa',
      place: 'Savassi',
      icon: 'color-palette',
      services: [
        { name: 'Escova', price: 'R$ 55' },
        { name: 'Coloração', price: 'R$ 180' },
      ],
    },
    related: ['barbearia', 'manicure', 'esteticista'],
    comparisons: ['agenda-no-whatsapp'],
  },
  {
    slug: 'manicure',
    label: 'Manicure e nail designer',
    ramo: 'manicure e nail designer',
    title: 'Agenda para manicure e nail designer',
    description: `Agenda e cobrança avulsa grátis para até ${FREE_LIMIT} clientes. Link para marcar horário, lembrete no WhatsApp e manutenção mensal nos planos pagos.`,
    h1: 'Agenda online para manicure e nail designer',
    sub: 'A cliente marca pelo link enquanto você atende.',
    answer: `A Controlapag é um app de agenda e cobrança para manicure e nail designer: a cliente marca pelo link, recebe o lembrete no WhatsApp e a manutenção mensal é cobrada sozinha. Grátis para até ${FREE_LIMIT} clientes por mês com cobrança avulsa; o resto nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'cliente',
    packSlug: 'manicure',
    leads: BELEZA,
    tagline: 'Mão ocupada, agenda andando.',
    painsTitle: 'Por que a manicure perde horário com falta e fiado?',
    painsIntro:
      'Porque não dá para responder mensagem com a mão no esmalte, e a cliente que esquece o horário deixa a agenda com um buraco que ninguém encaixa.',
    pains: [
      { icon: 'hand-left-outline', label: 'Sem mão livre para responder', text: 'A cliente pergunta horário no WhatsApp enquanto você está no meio do gel.' },
      { icon: 'calendar-clear-outline', label: 'Manutenção esquecida', text: 'A cliente passa da data da manutenção e só lembra quando a unha quebra.' },
      { icon: 'person-remove-outline', label: 'Falta no horário do gel', text: 'O horário reservado para a esmaltação em gel fica vazio.' },
      { icon: 'book-outline', label: 'Fiado no caderno', text: 'O "te passo o Pix depois" fica anotado e se perde.' },
    ],
    stepsTitle: 'Como a cliente da manicure marca e paga?',
    featuresTitle: 'O que a Controlapag faz pela manicure?',
    featuresIntro:
      'Deixa a cliente marcar sozinha e lembra o horário por você. A agenda e a cobrança avulsa são grátis; link, lembrete e manutenção mensal são pagos.',
    features: [
      { icon: 'link-outline', title: 'Agendamento pelo link', short: 'Cliente marca sozinha', paid: true },
      { icon: 'repeat-outline', title: 'Assinatura de manutenção', short: 'Manutenção todo mês', paid: true },
      { icon: 'notifications-outline', title: 'Lembrete automático', short: '1 dia antes, no WhatsApp', paid: true },
      { icon: 'card-outline', title: 'Recebe por fora ou pelo link', short: 'Link com taxa, nos planos pagos' },
      { icon: 'home-outline', title: 'Atendimento em domicílio', short: 'No seu espaço ou na casa dela' },
    ],
    defaultsTitle: 'Como a agenda da manicure já começa pronta?',
    faqTitle: 'O que a manicure pergunta antes de começar?',
    faq: [
      {
        question: 'Como a manicure pode organizar a agenda sem ficar no WhatsApp?',
        answer:
          'Com um link de agendamento que mostra só os horários livres. A cliente marca sozinha e você recebe o aviso do novo horário. O link está nos planos pagos.',
      },
      { question: 'Dá para cobrar sinal antes do atendimento?', answer: noDeposit('a cliente') },
      { question: 'Cliente faltou no horário do gel: posso cobrar?', answer: `Pode cobrar o que já estava combinado. ${noShow('o serviço')}` },
      {
        question: 'Como fazer pacote de manutenção mensal?',
        answer:
          'Crie uma assinatura mensal com a quantidade de manutenções do mês; o assistente já sugere uma. O que a cliente não usa não passa para o mês seguinte. Assinatura é dos planos pagos.',
      },
      {
        question: 'Funciona para quem atende em domicílio?',
        answer: 'Sim. Cada serviço pode ser no seu espaço, na casa da cliente ou online, e o lembrete leva o endereço do atendimento.',
      },
      {
        question: 'Quanto custa uma agenda para manicure?',
        answer: priceAnswer('clientes', 'Link de agendamento, lembrete no WhatsApp e assinatura de manutenção'),
      },
      {
        question: 'A cliente precisa baixar app para marcar horário?',
        answer: 'Não. Ela marca horário pelo link, no navegador do celular, e recebe o lembrete no WhatsApp (planos pagos).',
      },
      {
        question: 'Melhor ter página própria ou estar num app de marketplace?',
        answer:
          'Na página própria a cliente vê só você, seus serviços e seus horários, sem outras profissionais ao lado. O link vai na sua bio e no seu WhatsApp.',
      },
    ],
    mock: {
      business: 'Unhas da Carol',
      place: 'Moema',
      icon: 'sparkles',
      services: [
        { name: 'Esmaltação em gel', price: 'R$ 75' },
        { name: 'Manutenção', price: 'R$ 100' },
      ],
    },
    related: ['salao-de-beleza', 'esteticista', 'barbearia'],
    comparisons: ['marketplace-de-beleza'],
  },
  {
    slug: 'personal-trainer',
    label: 'Personal trainer',
    ramo: 'personal trainer',
    title: 'App para personal trainer cobrar alunos',
    description: `Agenda e cobrança avulsa grátis para até ${FREE_LIMIT} alunos. Mensalidade com contrato, cota de treinos e lembrete no WhatsApp nos planos pagos.`,
    h1: 'Agenda e cobrança de alunos para personal trainer',
    sub: 'Mensalidade cobrada sozinha. Treinos fixos na agenda.',
    answer: `A Controlapag é um app para personal trainer cobrar a mensalidade e organizar os treinos: o plano de 2 ou 3 treinos por semana é cobrado todo mês e o aluno recebe o lembrete no WhatsApp. Grátis para até ${FREE_LIMIT} alunos por mês com cobrança avulsa; mensalidade nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'aluno',
    packSlug: 'personal-trainer',
    leads: AULAS,
    tagline: 'Você treina. A mensalidade se cobra.',
    painsTitle: 'Por que o personal trainer perde dinheiro com mensalidade atrasada?',
    painsIntro:
      'Porque cobrar aluno por mensagem é desconfortável e o atraso de um mês vira dois quando ninguém lembra a data do vencimento.',
    pains: [
      { icon: 'cash-outline', label: 'Mensalidade atrasada', text: 'O dia 5 passa, você não quer cobrar no meio do treino e o Pix não vem.' },
      { icon: 'shuffle-outline', label: 'Reposição bagunçada', text: 'Aluno falta, pede reposição e ninguém sabe quantos treinos ele já fez na semana.' },
      { icon: 'document-outline', label: 'Combinado só de boca', text: 'Valor, dia de pagamento e horários ficam em mensagens soltas.' },
      { icon: 'trending-up-outline', label: 'Mês que vem incerto', text: 'Sem saber quem renova, você não sabe quanto vai entrar.' },
    ],
    stepsTitle: 'Como o aluno do personal marca e paga?',
    steps: [
      { title: 'O aluno marca pelo link', sub: 'Só horários livres. Sem baixar app.', mock: 'booking' },
      { title: 'O lembrete sai sozinho', sub: `${hours(AULAS.reminderHours)} antes do treino, no WhatsApp do aluno (planos pagos).`, mock: 'chat' },
      { title: 'Você vê quem pagou', sub: 'Recebido e a receber numa tela.', mock: 'charges' },
    ],
    featuresTitle: 'O que a Controlapag faz pelo personal trainer?',
    featuresIntro:
      'Cobra a mensalidade todo mês e conta os treinos de cada aluno. A agenda e a cobrança avulsa são grátis; mensalidade e contrato recorrente são pagos.',
    features: [
      { icon: 'repeat-outline', title: 'Mensalidade automática', short: 'Cobra todo mês', paid: true },
      { icon: 'document-text-outline', title: 'Contrato do aluno', short: 'Valor, vencimento e horários', paid: true },
      { icon: 'layers-outline', title: 'Cota de treinos', short: 'Treinos por semana', paid: true },
      { icon: 'calendar-outline', title: 'Treinos na agenda', short: 'Dia e hora de cada aluno' },
      { icon: 'stats-chart-outline', title: 'Painel do mês', short: 'Recebido e a receber' },
    ],
    defaultsTitle: 'Como a conta do personal trainer já começa pronta?',
    faqTitle: 'O que o personal pergunta antes de começar?',
    faq: [
      {
        question: 'Como o personal trainer deve cobrar a mensalidade do aluno?',
        answer: `Com cobrança recorrente no mesmo dia todo mês. Nos planos pagos, a Controlapag manda o lembrete da cobrança no WhatsApp ${CHARGE_DAYS} dias antes e no dia do vencimento, com o valor e o link para pagar.`,
      },
      {
        question: 'O que fazer com a mensalidade atrasada?',
        answer:
          'O lembrete para no dia do vencimento: nada sai depois. A partir daí a cobrança aparece como pendente no painel e você combina com o aluno. A Controlapag não cria multa nem valor extra.',
      },
      {
        question: 'Precisa de contrato com aluno de personal?',
        answer: `Não é obrigatório. Na Controlapag o contrato do aluno guarda o valor, o dia de vencimento e os horários dos treinos; ele não tem cláusula de falta. A regra que existe é o prazo mínimo para desmarcar, que o assistente sugere em ${hours(AULAS.cancelHours)}.`,
      },
      { question: 'Aluno faltou: o treino conta?', answer: noShow('o treino') },
      {
        question: 'Como controlar reposição de treino?',
        answer:
          'Remarque o treino dentro da mesma semana. A cota do plano é semanal e não acumula para a semana seguinte, e a agenda mostra os treinos já marcados.',
      },
      {
        question: 'Quanto custa um app para personal trainer?',
        answer: priceAnswer('alunos', 'Mensalidade, contrato recorrente e lembrete no WhatsApp'),
      },
      {
        question: 'O aluno precisa baixar app?',
        answer:
          'Não. Ele recebe os lembretes no WhatsApp e paga por fora ou pelo link, no navegador (planos pagos, com taxa). Se quiser, entra numa conta própria, grátis, para ver horários e cobranças.',
      },
      {
        question: 'Dá para atender em estúdio, na casa do aluno e online?',
        answer: 'Dá. Cada serviço tem o local: seu espaço, a casa do aluno ou online. No treino online, o lembrete leva o link da chamada.',
      },
    ],
    mock: {
      business: 'Treino com Rafa',
      place: 'Barra da Tijuca',
      icon: 'barbell',
      services: [
        { name: 'Treino avulso', price: 'R$ 100' },
        { name: 'Plano 3x por semana', price: 'R$ 700' },
      ],
      reminderAt: '22:20',
    },
    related: ['professor-particular', 'fisioterapeuta', 'psicologo'],
    comparisons: ['planilha', 'agenda-de-papel'],
  },
  {
    slug: 'professor-particular',
    label: 'Professor particular',
    ramo: 'professor particular',
    title: 'Agenda e mensalidade para professor particular',
    description: `Aulas na agenda e cobrança avulsa grátis para até ${FREE_LIMIT} alunos. Mensalidade, aulas fixas e lembrete ao responsável no WhatsApp nos planos pagos.`,
    h1: 'Organize alunos e mensalidades de aula particular',
    sub: 'Aulas fixas na agenda. Mensalidade lembrada sozinha.',
    answer: `A Controlapag é um app para professor particular organizar aulas e mensalidades: o plano de 1 ou 2 aulas por semana é cobrado todo mês e o responsável recebe o lembrete no WhatsApp. Grátis para até ${FREE_LIMIT} alunos por mês com cobrança avulsa; mensalidade nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'aluno',
    packSlug: 'professor-particular',
    leads: AULAS,
    tagline: 'Menos cobrança, mais aula.',
    painsTitle: 'Por que o professor particular perde aula e mensalidade?',
    painsIntro:
      'Porque a aula remarcada por mensagem some da cabeça e cada família paga num dia, então o atraso só aparece no fim do mês.',
    pains: [
      { icon: 'cash-outline', label: 'Responsável esquece de pagar', text: 'Quem paga é o pai ou a mãe, que não estava na aula e não lembra da data.' },
      { icon: 'chatbubbles-outline', label: 'Aula remarcada some', text: 'A troca combinada no WhatsApp não vai para agenda nenhuma.' },
      { icon: 'calendar-number-outline', label: 'Cada um paga num dia', text: 'Conferir quem pagou vira tarefa de domingo, aluno por aluno.' },
      { icon: 'time-outline', label: 'Aluno novo sem resposta', text: 'O interessado manda mensagem durante a sua aula e desiste antes da resposta.' },
    ],
    stepsTitle: 'Como a aula particular é marcada e paga?',
    steps: [
      { title: 'O aluno marca pelo link', sub: 'Só horários livres. Sem baixar app.', mock: 'booking' },
      { title: 'O lembrete sai sozinho', sub: `${hours(AULAS.reminderHours)} antes da aula, no WhatsApp cadastrado (planos pagos).`, mock: 'chat' },
      { title: 'Você vê quem pagou', sub: 'Recebido e a receber numa tela.', mock: 'charges' },
    ],
    featuresTitle: 'O que a Controlapag faz pelo professor particular?',
    featuresIntro:
      'Põe as aulas fixas na agenda e lembra a mensalidade por você. A agenda e a cobrança avulsa são grátis; mensalidade e lembrete são pagos.',
    features: [
      { icon: 'repeat-outline', title: 'Aulas fixas na agenda', short: 'Aula toda semana', paid: true },
      { icon: 'notifications-outline', title: 'Mensalidade com lembrete', short: 'Cadastre o WhatsApp do responsável', paid: true },
      { icon: 'globe-outline', title: 'Página para aluno novo', short: 'Link para a aula diagnóstica', paid: true },
      { icon: 'location-outline', title: 'Em casa, no seu espaço ou online', short: 'Local por serviço' },
    ],
    defaultsTitle: 'Como a conta do professor já começa pronta?',
    faqTitle: 'O que o professor pergunta antes de começar?',
    faq: [
      {
        question: 'Quanto cobrar por aula particular?',
        answer:
          'Depende da matéria, da cidade e da sua experiência. Parta do custo da sua hora (deslocamento, preparo e impostos). O assistente sugere valores de partida, que você muda quando quiser.',
      },
      {
        question: 'Como cobrar mensalidade de aula particular?',
        answer: `Combine um valor mensal pela quantidade de aulas por semana e deixe a cobrança recorrente. O lembrete sai ${CHARGE_DAYS} dias antes e no vencimento, com o valor e o link. Mensalidade é dos planos pagos.`,
      },
      {
        question: 'O lembrete vai para o aluno ou para o responsável?',
        answer:
          'Vai para o telefone cadastrado no aluno. Para criança e adolescente, cadastre o WhatsApp do responsável: ele recebe o lembrete da aula e o da mensalidade.',
      },
      { question: 'Aluno faltou: a aula conta?', answer: noShow('o atendimento') },
      {
        question: 'Dá para remarcar aula sem perder o controle?',
        answer: `Dá. Você muda só aquela aula na agenda e as outras semanas continuam iguais. Pelo link, o aluno só desmarca até ${hours(AULAS.cancelHours)} antes, o prazo que o assistente sugere.`,
      },
      {
        question: 'Serve para professor de música e de idiomas?',
        answer: 'Serve para qualquer aula com horário marcado: música, idiomas, reforço escolar, dança.',
      },
      {
        question: 'Quanto custa a Controlapag para professor?',
        answer: priceAnswer('alunos', 'Mensalidade, aulas fixas e lembrete no WhatsApp'),
      },
      {
        question: 'Ainda preciso de planilha?',
        answer: 'Não. O painel mostra o que entrou e o que falta receber no mês, aluno por aluno.',
      },
    ],
    mock: {
      business: 'Aulas com Marina',
      place: 'Pinheiros',
      icon: 'school',
      services: [
        { name: 'Aula de matemática', price: 'R$ 90' },
        { name: 'Plano 1x por semana', price: 'R$ 340' },
      ],
      reminderAt: '22:20',
    },
    related: ['personal-trainer', 'psicologo', 'fisioterapeuta'],
    comparisons: ['planilha', 'agenda-de-papel'],
  },
  {
    slug: 'psicologo',
    label: 'Psicólogo',
    ramo: 'psicólogo',
    title: 'Agenda e cobrança de sessões para psicólogo',
    description: `Agenda e cobrança por sessão grátis para até ${FREE_LIMIT} pacientes. Lembrete no WhatsApp, link para paciente novo e mensalidade nos planos pagos.`,
    h1: 'Agenda de sessões e cobrança para psicólogo',
    sub: 'Horário fixo do paciente e cobrança sem constrangimento.',
    answer: `A Controlapag é um app de agenda e cobrança para psicólogo: a sessão semanal fica fixa na agenda, o paciente recebe o lembrete no WhatsApp e a cobrança sai por sessão ou por mês. Grátis para até ${FREE_LIMIT} pacientes por mês com cobrança avulsa; o resto nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'paciente',
    packSlug: 'psicologo',
    leads: SAUDE,
    tagline: 'Você cuida. A agenda lembra por você.',
    painsTitle: 'Por que o psicólogo perde sessão e evita cobrar?',
    painsIntro:
      'Porque falar de dinheiro no fim da sessão é desconfortável e a falta sem aviso deixa um horário da semana vazio que ninguém ocupa.',
    pains: [
      { icon: 'person-remove-outline', label: 'Falta sem aviso', text: 'O paciente não vem e o horário fixo da semana fica parado.' },
      { icon: 'sad-outline', label: 'Cobrar é desconfortável', text: 'Lembrar o pagamento no fim da sessão atrapalha a conversa.' },
      { icon: 'swap-horizontal-outline', label: 'Remarcar dá trabalho', text: 'Trocar o horário fixo por mensagem gera ida e volta.' },
      { icon: 'grid-outline', label: 'Pagamento espalhado', text: 'Pix de um, transferência de outro, e nenhum lugar com o mês inteiro.' },
    ],
    stepsTitle: 'Como o paciente marca e paga a sessão?',
    steps: [
      { title: 'O paciente novo marca pelo link', sub: 'Só horários livres. Sem baixar app.', mock: 'booking' },
      { title: 'O lembrete sai sozinho', sub: 'Um dia antes, no WhatsApp do paciente (planos pagos). Use um nome neutro, como "Sessão".', mock: 'chat' },
      { title: 'Você vê quem pagou', sub: 'Recebido e a receber numa tela.', mock: 'charges' },
    ],
    featuresTitle: 'O que a Controlapag faz pelo consultório de psicologia?',
    featuresIntro:
      'Cuida da agenda e da cobrança, sem anotação clínica. A agenda e a cobrança por sessão são grátis; lembrete, link e mensalidade são pagos.',
    features: [
      { icon: 'calendar-outline', title: 'Horário fixo semanal', short: 'Mesmo horário toda semana' },
      { icon: 'wallet-outline', title: 'Por sessão ou mensal', short: 'Mensal nos planos pagos' },
      { icon: 'eye-off-outline', title: 'Preço visível ou não', short: 'Valor, "a partir de" ou sob consulta' },
      { icon: 'videocam-outline', title: 'Presencial ou online', short: 'Lembrete leva o link', paid: true },
      { icon: 'shield-checkmark-outline', title: 'Sem anotação clínica', short: 'Só agenda e pagamentos' },
    ],
    defaultsTitle: 'Como a agenda do psicólogo já começa pronta?',
    faqTitle: 'O que o psicólogo pergunta antes de começar?',
    faq: [
      {
        question: 'Como o psicólogo pode cobrar falta sem constrangimento?',
        answer: `Combine no primeiro atendimento o prazo para desmarcar; o assistente sugere ${hours(SAUDE.cancelHours)}. ${noShow('o atendimento')}`,
      },
      {
        question: 'Cobrança mensal ou por sessão: qual é melhor?',
        answer:
          'Mensal dá previsão para os dois lados quando a sessão é semanal e fixa; por sessão serve para atendimento esporádico. A cobrança por sessão é grátis; a mensalidade é dos planos pagos.',
      },
      {
        question: 'Preciso mostrar o preço na minha página?',
        answer: 'Não. Cada serviço mostra o valor, "a partir de" ou "Sob consulta". Quem esconde combina o valor no primeiro contato.',
      },
      {
        question: 'O que a Controlapag guarda do paciente?',
        answer:
          'Nome, telefone, horários, serviços e pagamentos. Não há campo de anotação clínica. O lembrete no WhatsApp leva o nome do serviço, então use um nome neutro, como "Sessão".',
      },
      {
        question: 'A Controlapag substitui o prontuário?',
        answer: 'Não. Ela cuida de agenda e cobrança. O prontuário e a evolução continuam onde você já guarda hoje.',
      },
      {
        question: 'Funciona para atendimento online?',
        answer: 'Funciona. No serviço online, o lembrete leva o link da chamada ou avisa que você envia o link antes da sessão.',
      },
      {
        question: 'Quanto custa a Controlapag para psicólogo?',
        answer: priceAnswer('pacientes', 'Lembrete no WhatsApp, link para paciente novo e mensalidade'),
      },
      {
        question: 'O paciente precisa baixar app?',
        answer:
          'Não. Ele recebe o lembrete no WhatsApp e, nos planos pagos, marca a primeira consulta e paga pelo link, no navegador, com taxa por pagamento.',
      },
    ],
    mock: {
      business: 'Helena Prado · Psicóloga',
      place: 'CRP 00/00000',
      icon: 'leaf',
      services: [
        { name: 'Sessão', price: 'Valor sob consulta' },
        { name: 'Sessão online', price: 'Valor sob consulta' },
      ],
      charge: 'R$ 180,00',
    },
    related: ['fisioterapeuta', 'personal-trainer', 'professor-particular'],
    comparisons: ['agenda-no-whatsapp'],
  },
  {
    slug: 'esteticista',
    label: 'Esteticista',
    ramo: 'esteticista',
    title: 'Agenda e cobrança para esteticista',
    description: `Agenda e cobrança avulsa grátis para até ${FREE_LIMIT} clientes. Plano mensal com cota de sessões, link para marcar e lembrete no WhatsApp nos planos pagos.`,
    h1: 'Agenda e plano mensal para esteticista',
    sub: 'Sessões do mês contadas e lembrete da próxima.',
    answer: `A Controlapag é um app de agenda e cobrança para esteticista: a cliente marca pelo link, recebe o lembrete no WhatsApp e o plano mensal conta as sessões de cada mês. Grátis para até ${FREE_LIMIT} clientes por mês com cobrança avulsa; o resto nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'cliente',
    packSlug: 'estetica',
    leads: BELEZA,
    tagline: 'Cada sessão contada, cada mês em dia.',
    painsTitle: 'Por que a esteticista perde cliente no meio do tratamento?',
    painsIntro:
      'Porque a próxima sessão fica para "depois eu marco" e as sessões do mês ficam num caderno que só você vê.',
    pains: [
      { icon: 'book-outline', label: 'Sessões no caderno', text: 'Quantas sessões a cliente já fez no mês fica na memória.' },
      { icon: 'person-remove-outline', label: 'Cliente some no meio', text: 'Sem a próxima sessão marcada, o tratamento para na terceira semana.' },
      { icon: 'git-merge-outline', label: 'Horário encavalado', text: 'Drenagem marcada em cima da limpeza de pele porque a duração não foi conferida.' },
      { icon: 'cash-outline', label: 'Pagamento sem data', text: 'Combinou de pagar no fim do mês e ninguém lembrou.' },
    ],
    stepsTitle: 'Como a cliente da estética marca e paga?',
    featuresTitle: 'O que a Controlapag faz pela esteticista?',
    featuresIntro:
      'Conta as sessões do plano mensal e lembra a próxima. A agenda e a cobrança avulsa são grátis; plano mensal, link e lembrete são pagos.',
    features: [
      { icon: 'layers-outline', title: 'Plano mensal com cota', short: 'Sessões do mês, sem acumular', paid: true },
      { icon: 'time-outline', title: 'Duração por procedimento', short: 'Sem horário encavalado' },
      { icon: 'notifications-outline', title: 'Lembrete da próxima sessão', short: '1 dia antes, no WhatsApp', paid: true },
      { icon: 'link-outline', title: 'Agendamento pelo link', short: 'Cliente marca sozinha', paid: true },
      { icon: 'card-outline', title: 'Recebe por fora ou pelo link', short: 'Link com taxa, nos planos pagos' },
    ],
    defaultsTitle: 'Como a agenda da estética já começa pronta?',
    faqTitle: 'O que a esteticista pergunta antes de começar?',
    faq: [
      {
        question: 'Como controlar as sessões de cada cliente?',
        answer:
          'Crie um plano mensal com a quantidade de sessões do mês. Cada horário usa uma sessão e o que sobra não passa para o mês seguinte. O plano mensal é dos planos pagos.',
      },
      {
        question: 'Dá para vender pacote de 10 sessões com saldo?',
        answer:
          'Não. A Controlapag não tem pacote fechado com saldo nem parcelamento. O que existe é o plano mensal com cota de sessões que não acumula, ou a sessão avulsa.',
      },
      {
        question: 'Como evitar que a cliente abandone o tratamento?',
        answer: `Deixe as próximas sessões já marcadas e use o lembrete no WhatsApp (planos pagos), que sai ${hours(BELEZA.reminderHours)} antes de cada uma.`,
      },
      { question: 'Cliente faltou: perde a sessão?', answer: `No plano mensal, perde. ${noShow('o procedimento')}` },
      { question: 'Dá para cobrar sinal do procedimento?', answer: noDeposit('a cliente') },
      { question: 'Estúdio com várias profissionais pode usar?', answer: teamAnswer('profissional', 'do estúdio') },
      {
        question: 'Página própria ou marketplace de beleza?',
        answer:
          'Na página própria a cliente vê só os seus procedimentos e horários, e o link vai na sua bio. A página com agenda está nos planos pagos.',
      },
      {
        question: 'Quanto custa a Controlapag para esteticista?',
        answer: priceAnswer('clientes', 'Plano mensal com cota, link de agendamento e lembrete no WhatsApp'),
      },
    ],
    mock: {
      business: 'Clínica Pele Leve',
      place: 'Jardins',
      icon: 'flower',
      services: [
        { name: 'Limpeza de pele', price: 'R$ 150' },
        { name: 'Plano mensal 4 sessões', price: 'R$ 480' },
      ],
    },
    related: ['salao-de-beleza', 'manicure', 'fisioterapeuta'],
    comparisons: ['marketplace-de-beleza'],
  },
  {
    slug: 'fisioterapeuta',
    label: 'Fisioterapeuta',
    ramo: 'fisioterapeuta',
    title: 'Agenda de sessões para fisioterapeuta',
    description: `Sessões do tratamento na agenda e cobrança avulsa grátis para até ${FREE_LIMIT} pacientes. Lembrete no WhatsApp e mensalidade nos planos pagos.`,
    h1: 'Agenda de tratamento e cobrança para fisioterapeuta',
    sub: 'Marque o tratamento inteiro de uma vez. O paciente é lembrado.',
    answer: `A Controlapag é um app de agenda e cobrança para fisioterapeuta: as sessões do tratamento entram na agenda de uma vez, o paciente recebe o lembrete no WhatsApp e cada visita domiciliar guarda o endereço. Grátis para até ${FREE_LIMIT} pacientes por mês com cobrança avulsa; o resto nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'paciente',
    packSlug: 'fisioterapeuta',
    leads: SAUDE,
    tagline: 'Tratamento inteiro na agenda.',
    painsTitle: 'Por que o fisioterapeuta perde tempo com a agenda do tratamento?',
    painsIntro:
      'Porque marcar as sessões uma a uma toma a manhã, e cada falta empurra o fim do tratamento para mais longe.',
    pains: [
      { icon: 'calendar-number-outline', label: '20 sessões uma a uma', text: 'Cada retorno marcado na mão, com risco de pular uma semana.' },
      { icon: 'person-remove-outline', label: 'Falta atrasa o tratamento', text: 'A sessão que não aconteceu precisa de outro horário.' },
      { icon: 'location-outline', label: 'Endereço de cada visita perdido', text: 'No domiciliar, o endereço fica numa conversa antiga do WhatsApp.' },
      { icon: 'alert-circle-outline', label: 'Pagamento pendente', text: 'No fim do tratamento ninguém sabe quantas sessões foram pagas.' },
    ],
    stepsTitle: 'Como o tratamento entra na agenda e é cobrado?',
    steps: [
      { title: 'Você marca o tratamento de uma vez', sub: 'Dias da semana e período. Se o paciente remarcar uma sessão, só ela muda.', mock: 'booking' },
      { title: 'O lembrete sai sozinho', sub: 'Um dia antes, no WhatsApp do paciente (planos pagos).', mock: 'chat' },
      { title: 'Você vê quem pagou', sub: 'Recebido e a receber numa tela.', mock: 'charges' },
    ],
    featuresTitle: 'O que a Controlapag faz pelo fisioterapeuta?',
    featuresIntro:
      'Põe o tratamento inteiro na agenda e lembra o paciente de cada sessão. A agenda e a cobrança avulsa são grátis; lembrete e mensalidade são pagos.',
    features: [
      { icon: 'repeat-outline', title: 'Sessões recorrentes', short: 'Tratamento de uma vez' },
      { icon: 'wallet-outline', title: 'Por sessão ou mensalidade', short: 'Mensalidade nos planos pagos' },
      { icon: 'home-outline', title: 'Atendimento domiciliar', short: 'Endereço em cada visita' },
      { icon: 'notifications-outline', title: 'Lembrete para o paciente', short: '1 dia antes, no WhatsApp', paid: true },
    ],
    defaultsTitle: 'Como a agenda do fisioterapeuta já começa pronta?',
    faqTitle: 'O que o fisioterapeuta pergunta antes de começar?',
    faq: [
      {
        question: 'Como organizar a agenda de fisioterapia com muitas sessões?',
        answer:
          'Marque o tratamento de uma vez, com os dias da semana e o período. Se o paciente remarcar uma sessão, só aquela muda.',
      },
      { question: 'Paciente faltou: o que acontece com a sessão?', answer: noShow('o atendimento') },
      {
        question: 'Dá para cobrar por sessão e por mês?',
        answer:
          'Dá. A sessão avulsa é grátis; a mensalidade, como a do pilates clínico 2 vezes por semana, é dos planos pagos. A Controlapag não tem pacote fechado com saldo.',
      },
      {
        question: 'Funciona para atendimento domiciliar?',
        answer: 'Funciona. O serviço fica marcado como atendimento na casa do paciente e o lembrete leva o endereço da visita.',
      },
      {
        question: 'Serve para estúdio de pilates?',
        answer: 'Serve. Mensalidade por frequência semanal e cota de aulas que não acumula funcionam do mesmo jeito.',
      },
      {
        question: 'A Controlapag substitui o prontuário?',
        answer: 'Não. Ela cuida da agenda e da cobrança. Não há campo de anotação clínica; a evolução continua no seu prontuário.',
      },
      {
        question: 'Quanto custa a Controlapag para fisioterapeuta?',
        answer: priceAnswer('pacientes', 'Lembrete no WhatsApp, link de agendamento e mensalidade'),
      },
      {
        question: 'O paciente precisa baixar app?',
        answer: 'Não. Ele recebe o lembrete no WhatsApp e, nos planos pagos, paga pelo link no navegador, com taxa por pagamento.',
      },
    ],
    mock: {
      business: 'Fisio Movimento',
      place: 'Centro',
      icon: 'fitness',
      services: [
        { name: 'Sessão de fisioterapia', price: 'R$ 120' },
        { name: 'Avaliação', price: 'R$ 150' },
      ],
    },
    related: ['psicologo', 'personal-trainer', 'esteticista'],
    comparisons: ['agenda-no-whatsapp'],
  },
  {
    slug: 'diarista',
    label: 'Diarista',
    ramo: 'diarista',
    title: 'Agenda e cobrança para diarista',
    description: `Agenda das faxinas e cobrança avulsa grátis para até ${FREE_LIMIT} clientes. Diária semanal por contrato e lembrete no WhatsApp nos planos pagos.`,
    h1: 'Agenda de faxinas e cobrança para diarista',
    sub: 'Cada casa no dia certo. A cobrança lembrada sozinha.',
    answer: `A Controlapag é um app de agenda e cobrança para diarista: cada faxina fica na agenda com o endereço da cliente, a diária semanal vira contrato e o lembrete sai no WhatsApp. Grátis para até ${FREE_LIMIT} clientes por mês com cobrança avulsa; contrato recorrente e lembrete nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'cliente',
    packSlug: 'diarista',
    leads: SAUDE,
    tagline: 'Cada casa no dia certo.',
    painsTitle: 'Por que a diarista perde dia de trabalho e pagamento?',
    painsIntro:
      'Porque a cliente desmarca na véspera e o dia fica vazio, e o pagamento de cada casa fica para "sexta eu te passo".',
    pains: [
      { icon: 'calendar-clear-outline', label: 'Cancelamento na véspera', text: 'O dia de uma casa some e não dá tempo de encaixar outra.' },
      { icon: 'location-outline', label: 'Endereço em conversa antiga', text: 'O endereço e o ponto de referência ficam perdidos no WhatsApp.' },
      { icon: 'cash-outline', label: 'Pagamento para depois', text: 'Cada casa paga num dia e o atraso só aparece no fim do mês.' },
      { icon: 'help-circle-outline', label: 'Faxina pesada sem preço', text: 'A casa pós-obra pede orçamento, e o valor combinado some da conversa.' },
    ],
    stepsTitle: 'Como a cliente da diarista marca e paga?',
    steps: [
      { title: 'A cliente marca a diária pelo link', sub: 'Escolhe um dia livre e pode repetir toda semana.', mock: 'booking' },
      { title: 'O lembrete sai sozinho', sub: 'Um dia antes, no WhatsApp da cliente, com o endereço (planos pagos).', mock: 'chat' },
      { title: 'Você vê quem pagou', sub: 'Diárias recebidas e a receber numa tela.', mock: 'charges' },
    ],
    featuresTitle: 'O que a Controlapag faz pela diarista?',
    featuresIntro:
      'Organiza as casas da semana e lembra a cliente por você. A agenda e a cobrança avulsa são grátis; contrato recorrente e lembrete são pagos.',
    features: [
      { icon: 'repeat-outline', title: 'Diária toda semana', short: 'Contrato semanal, quinzenal ou mensal', paid: true },
      { icon: 'location-outline', title: 'Endereço de cada casa', short: 'No atendimento e no lembrete' },
      { icon: 'pricetag-outline', title: 'Faxina pesada sob consulta', short: 'Sem preço na página' },
      { icon: 'notifications-outline', title: 'Lembrete da faxina', short: '1 dia antes, no WhatsApp', paid: true },
      { icon: 'wallet-outline', title: 'Quem pagou e quem deve', short: 'Painel do mês' },
    ],
    defaultsTitle: 'Como a agenda da diarista já começa pronta?',
    faqTitle: 'O que a diarista pergunta antes de começar?',
    faq: [
      {
        question: 'Como cobrar a diária de quem tem faxina toda semana?',
        answer:
          'Faça um contrato semanal com o valor da diária: a cobrança sai toda semana e o lembrete vai no WhatsApp no dia do vencimento. Contrato com cobrança recorrente é dos planos pagos.',
      },
      {
        question: 'E a faxina quinzenal?',
        answer: 'Cobre uma vez por mês pelas 2 visitas. O assistente já sugere o contrato de faxina quinzenal com cobrança mensal.',
      },
      {
        question: 'A cliente desmarcou em cima da hora: posso cobrar?',
        answer: `Pelo link, a cliente só desmarca até ${hours(SAUDE.cancelHours)} antes, o prazo que o assistente sugere para diarista. ${noShow('o atendimento')}`,
      },
      {
        question: 'Como cobrar faxina pesada sem preço fixo?',
        answer:
          'Deixe o serviço sem preço na página e combine depois de ver a casa. Ele fica fora da agenda online e você cria a cobrança com o valor fechado.',
      },
      {
        question: 'A cliente precisa baixar app?',
        answer: 'Não. Ela marca pelo link, no navegador (planos pagos), e recebe o lembrete no WhatsApp com o endereço do atendimento.',
      },
      {
        question: 'Quanto custa a Controlapag para diarista?',
        answer: priceAnswer('clientes', 'Contrato semanal, link de agendamento e lembrete no WhatsApp'),
      },
      { question: 'Como a cliente paga a diária?', answer: payAnswer('a cliente') },
    ],
    mock: {
      business: 'Limpeza com Sandra',
      place: 'Zona Sul',
      icon: 'home',
      services: [
        { name: 'Diária de limpeza', price: 'R$ 180' },
        { name: 'Meia diária', price: 'R$ 130' },
      ],
      reminderExtra: 'Endereço: Rua das Acácias, 120',
    },
    related: ['personal-trainer', 'transporte-escolar', 'manicure'],
    comparisons: ['agenda-no-whatsapp'],
  },
  {
    slug: 'transporte-escolar',
    label: 'Transporte escolar',
    ramo: 'transporte escolar',
    title: 'App para cobrar mensalidade do transporte escolar',
    description: `Cobrança avulsa grátis para até ${FREE_LIMIT} clientes. Mensalidade com contrato e lembrete no WhatsApp para o responsável nos planos pagos.`,
    h1: 'Cobrança da mensalidade do transporte escolar',
    sub: 'Cada responsável no dia certo. O lembrete sai sozinho.',
    answer: `A Controlapag é um app para quem faz transporte escolar cobrar a mensalidade: cada responsável tem um contrato com o valor e o dia de vencimento, e o lembrete sai no WhatsApp ${CHARGE_DAYS} dias antes e no dia. Grátis para até ${FREE_LIMIT} clientes por mês com cobrança avulsa; mensalidade nos planos pagos, ${FROM_PRICE}.`,
    clientWord: 'cliente',
    packSlug: 'transporte-escolar',
    leads: { cancelHours: 24, reminderHours: null },
    badges: ['Comece grátis', 'Responsável não precisa baixar app'],
    tagline: 'A rota cheia e o mês pago.',
    painsTitle: 'Por que o transporte escolar perde mensalidade?',
    painsIntro:
      'Porque cada família paga num dia e o controle fica no caderno; cobrar uma por uma por mensagem toma a noite.',
    pains: [
      { icon: 'book-outline', label: 'Caderno de quem pagou', text: 'A conferência do mês depende de achar a anotação certa.' },
      { icon: 'cash-outline', label: 'Mensalidade atrasada', text: 'O dia 5 passa e você não sabe quem já mandou o Pix.' },
      { icon: 'sad-outline', label: 'Vergonha de cobrar', text: 'Mandar mensagem para cada pai e mãe parece cobrança de dívida.' },
      { icon: 'people-outline', label: 'Cada família, um valor', text: 'Ida e volta, só ida, só volta: cada responsável paga um valor.' },
    ],
    stepsTitle: 'Como a mensalidade do transporte é cobrada?',
    steps: [
      { title: 'Você cadastra o responsável e a rota', sub: 'Ida e volta, só ida ou só volta, com o valor e o vencimento.', mock: 'charges' },
      { title: 'O lembrete da mensalidade sai sozinho', sub: `${CHARGE_DAYS} dias antes e no vencimento, no WhatsApp do responsável (planos pagos).`, mock: 'chat' },
      { title: 'Você vê quem pagou', sub: 'Recebido e a receber do mês numa tela.', mock: 'charges' },
    ],
    featuresTitle: 'O que a Controlapag faz pelo transporte escolar?',
    featuresIntro:
      'Cobra a mensalidade de cada família no dia certo e mostra quem pagou. A cobrança avulsa é grátis; mensalidade com contrato e lembrete são pagos.',
    features: [
      { icon: 'repeat-outline', title: 'Mensalidade com contrato', short: 'Valor e vencimento fixos', paid: true },
      { icon: 'logo-whatsapp', title: 'Lembrete da mensalidade', short: 'Antes e no dia do vencimento', paid: true },
      { icon: 'wallet-outline', title: 'Quem pagou e quem falta', short: 'Painel do mês' },
      { icon: 'card-outline', title: 'Recebe por fora ou pelo link', short: 'Link com taxa, nos planos pagos' },
      { icon: 'people-outline', title: 'Cadastro do responsável', short: 'Quem paga é o responsável' },
    ],
    defaultsTitle: 'Como a conta do transporte escolar já começa pronta?',
    faqTitle: 'O que o motorista escolar pergunta antes de começar?',
    faq: [
      {
        question: 'Como cobrar a mensalidade do transporte escolar?',
        answer: `Faça um contrato por responsável com o valor e o dia de vencimento. A Controlapag gera a cobrança todo mês e manda o lembrete no WhatsApp ${CHARGE_DAYS} dias antes e no dia. Mensalidade com contrato é dos planos pagos.`,
      },
      {
        question: 'Dá para usar de graça?',
        answer: `Dá, com até ${FREE_LIMIT} clientes por mês: você cadastra cada responsável, cria a cobrança avulsa do mês e o painel mostra quem pagou. A mensalidade automática e o lembrete no WhatsApp estão nos planos pagos.`,
      },
      {
        question: 'O responsável atrasou: o que acontece?',
        answer:
          'Nada sai depois do vencimento. A cobrança fica pendente no painel e você combina com a família. A Controlapag não cria multa nem valor extra.',
      },
      {
        question: 'Como cobrar ida e volta, só ida e só volta?',
        answer: 'Cada rota é um serviço com o próprio valor. O assistente já cria ida e volta, só ida, só volta, taxa de matrícula e passeio.',
      },
      {
        question: 'O responsável precisa baixar app?',
        answer:
          'Não. Ele recebe o lembrete no WhatsApp e paga por fora ou pelo link, no navegador (planos pagos, com taxa por pagamento). Se quiser, entra numa conta própria, grátis, para ver as cobranças.',
      },
      {
        question: 'Aceita boleto?',
        answer: `Aceita, se você ligar o boleto no recebimento pelo link, nos planos pagos. Cada pagamento pelo link tem taxa: ${PROSE.pixFee} e ${PROSE.boletoFee}.`,
      },
      {
        question: 'Precisa de agenda de horário para o transporte?',
        answer:
          'Não. A rota tem dia e hora fixos, então o assistente deixa a agenda online e o lembrete de horário desligados. O que sai é o lembrete da mensalidade.',
      },
      {
        question: 'Quanto custa a Controlapag para transporte escolar?',
        answer: priceAnswer('clientes', 'Mensalidade com contrato e lembrete no WhatsApp'),
      },
    ],
    mock: {
      business: 'Van Escolar Caminho',
      place: 'Zona Norte',
      icon: 'bus',
      services: [
        { name: 'Ida e volta', price: 'R$ 320' },
        { name: 'Só ida', price: 'R$ 200' },
      ],
      appointmentReminder: false,
      kind: 'monthly',
    },
    related: ['diarista', 'professor-particular', 'personal-trainer'],
    comparisons: ['agenda-no-whatsapp'],
  },
];

/**
 * Nomes curtos que as pessoas digitam ou falam ("/para/salao"). Levam à URL
 * canônica. A área técnica transforma em redirect 308 no `vercel.json`.
 */
export const SEGMENT_ALIASES: Record<string, string> = {
  salao: 'salao-de-beleza',
  cabeleireira: 'salao-de-beleza',
  cabeleireiro: 'salao-de-beleza',
  barbeiro: 'barbearia',
  personal: 'personal-trainer',
  professor: 'professor-particular',
  'aula-particular': 'professor-particular',
  psicologa: 'psicologo',
  estetica: 'esteticista',
  fisioterapia: 'fisioterapeuta',
  pilates: 'fisioterapeuta',
  'nail-designer': 'manicure',
  faxineira: 'diarista',
  faxina: 'diarista',
  'van-escolar': 'transporte-escolar',
  'perua-escolar': 'transporte-escolar',
};

export const findSegmentPage = (slug: string): SegmentPage | undefined => {
  const key = slug.trim().toLowerCase();
  const canonical = SEGMENT_ALIASES[key] ?? key;
  return SEGMENT_PAGES.find((page) => page.slug === canonical);
};

// ------------------------------------------------ padrões do assistente

/** Um pacote do snapshot `content/generated/packs.json`. */
export interface PackSnapshotService {
  name: string;
  price: number | null;
  priceDisplayMode: string;
  durationMinutes: number | null;
  billingModel: 'RECURRING' | 'ONE_TIME';
  interval: string | null;
  isSubscription: boolean;
  quota: number | null;
  quotaPeriod: 'WEEK' | 'MONTH' | null;
}

export interface PackSnapshotTemplate {
  name: string;
  billingModel: 'RECURRING' | 'ONE_TIME';
  interval: string | null;
  chargeDay: number | null;
  reminderType: string | null;
}

export interface PackSnapshot {
  slug: string;
  label: string;
  categorySlug: string;
  enrollmentTerm: string | null;
  services: PackSnapshotService[];
  minCancelLeadMinutes: number;
  reminderEnabled: boolean;
  reminderLeadMinutes: number;
  contractTemplates: PackSnapshotTemplate[];
}

const INTERVAL_LABEL: Record<string, string> = {
  WEEKLY: 'por semana',
  MONTHLY: 'por mês',
  BIMONTHLY: 'a cada 2 meses',
  TRIMESTERLY: 'a cada 3 meses',
  SEMIANNUALLY: 'a cada 6 meses',
  YEARLY: 'por ano',
};

const REMINDER_TYPE_LABEL: Record<string, string> = {
  ON_DUE_DATE: 'no dia do vencimento',
  ONLY_ON_DUE_DATE: 'no dia do vencimento',
  THREE_DAYS_BEFORE_AND_ON_DUE: '3 dias antes e no vencimento',
  FIVE_DAYS_BEFORE_AND_ON_DUE: '5 dias antes e no vencimento',
  CUSTOM_DAYS_BEFORE_AND_ON_DUE: 'nos dias escolhidos e no vencimento',
};

/** O site não usa travessão: "Mensalidade — só ida" vira "Mensalidade: só ida". */
const cleanName = (name: string) => name.replace(/\s+—\s+/g, ': ');

const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
};

/** "até 2 horas antes", "até 1 dia antes". */
export const formatLead = (minutes: number): string => {
  if (minutes === 0) return 'sem prazo';
  if (minutes % 1440 === 0 && minutes > 1440) return `${minutes / 1440} dias`;
  const h = minutes / 60;
  return Number.isInteger(h) ? hours(h) : `${minutes} minutos`;
};

const priceCell = (service: PackSnapshotService): string => {
  if (service.price === null || service.priceDisplayMode === 'hidden') return 'Combinado no orçamento';
  const value = formatBRL(service.price);
  const base = service.priceDisplayMode === 'from' ? `a partir de ${value}` : value;
  if (service.isSubscription && service.interval) return `${base} ${INTERVAL_LABEL[service.interval] ?? ''}`.trim();
  return base;
};

const billingCell = (service: PackSnapshotService, pack: PackSnapshot): string => {
  if (service.isSubscription) {
    const period = service.quotaPeriod === 'WEEK' ? 'semana' : 'mês';
    return `Plano ${INTERVAL_LABEL[service.interval ?? 'MONTHLY'] === 'por mês' ? 'mensal' : 'recorrente'}, ${service.quota} por ${period} (pago)`;
  }
  if (service.billingModel === 'RECURRING' && service.interval) {
    return `Recorrente ${INTERVAL_LABEL[service.interval] ?? ''} (pago)`;
  }
  // Transporte escolar: o serviço é avulso, mas a mensalidade vem do contrato do pacote.
  const monthlyTemplate = pack.contractTemplates.find((t) => t.billingModel === 'RECURRING');
  if (monthlyTemplate && /^mensalidade/i.test(service.name)) {
    return 'Mensal pelo contrato (pago)';
  }
  return 'Avulsa (grátis)';
};

/** Linhas da tabela de serviços sugeridos: serviço, preço, duração, cobrança. */
export const packServiceRows = (pack: PackSnapshot): string[][] =>
  pack.services.map((service) => [
    cleanName(service.name),
    priceCell(service),
    service.durationMinutes ? formatMinutes(service.durationMinutes) : 'Sem horário marcado',
    billingCell(service, pack),
  ]);

/** Linhas de regra: prazo para desmarcar, lembrete, cota e mensalidade. */
export const packRuleRows = (pack: PackSnapshot): string[][] => {
  // Ramo sem horário para o cliente marcar (transporte escolar): prazo para
  // desmarcar e falta não se aplicam.
  const bookable = pack.services.some((service) => service.durationMinutes);
  const rows: string[][] = [
    [
      'Prazo para o cliente desmarcar pelo link',
      `Até ${formatLead(pack.minCancelLeadMinutes)} antes`,
      'Vale em qualquer plano; o link é dos planos pagos',
    ],
    [
      'Lembrete do horário no WhatsApp',
      pack.reminderEnabled && pack.reminderLeadMinutes > 0
        ? `${formatLead(pack.reminderLeadMinutes)[0].toUpperCase()}${formatLead(pack.reminderLeadMinutes).slice(1)} antes, 1 por horário`
        : 'Desligado: a rota tem dia e hora fixos',
      'Planos pagos',
    ],
    [
      'Falta',
      `Você marca em até ${NO_SHOW_DAYS} dias; a cobrança avulsa fica em aberto e, no plano, conta como sessão usada`,
      'Qualquer plano',
    ],
  ].filter((row) => bookable || row[0] === 'Lembrete do horário no WhatsApp');
  const quota = pack.services.find((service) => service.isSubscription && service.quota);
  if (quota) {
    rows.push([
      'Cota do plano',
      `Não acumula de um${quota.quotaPeriod === 'WEEK' ? 'a semana para a outra' : ' mês para o outro'}`,
      'Planos pagos',
    ]);
  }
  const template = pack.contractTemplates.find((t) => t.billingModel === 'RECURRING' && t.interval === 'MONTHLY');
  if (template) {
    rows.push([
      'Lembrete da mensalidade',
      `${template.chargeDay ? `Vence no dia ${template.chargeDay}; lembrete ` : 'Lembrete '}${REMINDER_TYPE_LABEL[template.reminderType ?? 'ON_DUE_DATE']}`,
      'Planos pagos',
    ]);
  }
  return rows;
};
